import { createSelector } from 'reselect'
import {
  Showing,
  checkIfReservedShowing,
  ReservedShowing,
  ReserveData
} from '../types/showings'
import {
  StateInterface,
  EmptyShowingsInterface,
  ShowingsInterface
} from '../types/states'
import { compareStartDates } from '../utils/sorting'

const dateNow = new Date()

const selectShowingsDomain = (state: StateInterface) => state.showings

const selectShowingsInterfaces = createSelector(
  selectShowingsDomain,
  (showings) => showings.showingsInterfaces
)
const selectShowingsLoading = createSelector(
  selectShowingsDomain,
  (showings) => showings.loading
)
const selectShowingsError = createSelector(
  selectShowingsDomain,
  (showings) => showings.error
)

const selectReservableApartmentShowings = (apartmentId: string) =>
  createSelector(
    selectShowingsInterfaces,
    (showingsInterfaces) =>
      showingsInterfaces[apartmentId]?.reservableShowings ||
      EmptyShowingsInterface
  )

const selectShowingsInterfaceByApartmentId = (apartmentId: string) =>
  createSelector(
    selectShowingsInterfaces,
    (showingsInterfaces) =>
      (showingsInterfaces[apartmentId] as ShowingsInterface) ||
      EmptyShowingsInterface
  )

const selectShowingsByApartmentId = (apartmentId: string) =>
  createSelector(selectShowingsInterfaces, (showingsInterfaces) =>
    apartmentId && showingsInterfaces
      ? showingsInterfaces[apartmentId]?.showings || []
      : []
  )

const selectReservableShowingsByApartmentId = (apartmentId: string) =>
  createSelector(
    selectShowingsInterfaceByApartmentId(apartmentId),
    (showingsInterface) => showingsInterface?.reservableShowings || []
  )

const selectFutureShowings = (showings: Showing[]) =>
  showings
    .filter(({ startDate }) => new Date(startDate) > dateNow)
    .sort(compareStartDates)

const selectFutureReservedShowingsByApartmentId = (apartmentId: string) =>
  createSelector(
    selectShowingsInterfaceByApartmentId(apartmentId),
    (showingsInterface) =>
      selectFutureShowings(
        showingsInterface?.reservations || []
      ) as ReservedShowing[]
  )

const selectFutureReservedShowings = (showings: Showing[]) =>
  showings
    .filter(
      (showing) =>
        new Date(showing.startDate) > dateNow && checkIfReservedShowing(showing)
    )
    .sort(compareStartDates) as ReservedShowing[]

const selectFutureShowingsByApartmentId = (apartmentId: string) =>
  createSelector(selectShowingsByApartmentId(apartmentId), (showings) =>
    selectFutureShowings(showings)
  )

const selectReserve = createSelector(
  selectShowingsDomain,
  (showings) => showings.reserve
)
const selectReserveData = createSelector(selectReserve, (reserve) =>
  reserve.reserveData ? (reserve.reserveData as ReserveData) : {}
)
const selectReserveLoading = createSelector(
  selectReserve,
  (reserve) => reserve.loading
)
const selectReserveViewLogic = createSelector(
  selectReserve,
  (reserve) => reserve.reserveViewLogic
)
const selectReserveApartmentId = createSelector(
  selectReserve,
  (reserve) => reserve.reserveData?.apartment.apartmentId
)
const selectReserveApplicationId = createSelector(
  selectReserve,
  (reserve) => reserve.reserveData?.application.applicationId
)
const selectReserveStatus = createSelector(
  selectReserve,
  (reserve) => reserve.reserveData?.application.status
)

const selectReserveInvited = createSelector(
  selectReserve,
  (reserve) => reserve.reserveData?.invited
)

export {
  selectShowingsDomain,
  selectShowingsInterfaces,
  selectShowingsInterfaceByApartmentId,
  selectShowingsByApartmentId,
  selectReservableApartmentShowings,
  selectShowingsLoading,
  selectReservableShowingsByApartmentId,
  selectFutureShowingsByApartmentId,
  selectFutureShowings,
  selectFutureReservedShowingsByApartmentId,
  selectFutureReservedShowings,
  selectReserve,
  selectReserveData,
  selectReserveViewLogic,
  selectReserveApartmentId,
  selectReserveApplicationId,
  selectReserveStatus,
  selectReserveInvited,
  selectReserveLoading,
  selectShowingsError
}
